import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Button, Col, Collapse, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap';
import OrderWorkflowActionButton from './OrderWorkflowActionButton';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useOrderCollaborationLock } from '../dashboard/processing-order/modal/common/OrderCollaborationLockContext';
import { useOrderDetailViewOnly } from '../dashboard/processing-order/modal/common/OrderDetailViewOnlyContext';
import { useOrderContext } from '../../providers/OrderProvider';
import { patchOrderDetailsCache, useAcceptOrderMutation, useDeliverOrderMutation } from '../../slices/ordersApiSlice';
import { socket } from '../../socket';
import formatDate, { pickValidDate } from '../../utils/formatDate';
import { markLocalOrderMutation } from '../../utils/order/orderLocalMutationGuard';
import { isScheduleGroupDone } from '../../utils/order/orderLogisticsTimeline.mjs';
import { canAcceptOrder, canDeliverOrder } from '../../utils/order/orderFulfillmentActions';

/**
 * 订单交付状态步骤组件
 * 根据付款节点动态显示发货、验收、完成步骤的状态
 * - 发货/提供服务步骤数量 = 付款节点中"发货/提供服务"节点的数量（至少1个）
 * - 验收步骤数量 = 付款节点中"验收"节点的数量（至少1个）
 */
const resolveStepCompletedAt = (node, orderAt, orderResult) => pickValidDate(node?.completedAt, node?.completedResult?.update_time, orderAt, orderResult?.update_time);

/** 待办主区（待签订单）：默认可见的发货步骤数 */
const DELIVERY_FOCUS_VISIBLE_STEP_COUNT = 2;

const OrderDeliveryStatus = ({ pendingStepFocus }) => {
	const deliveryCompact = pendingStepFocus === 'delivery';
	const viewOnly = useOrderDetailViewOnly();
	const dispatch = useDispatch();
	const { userInfo } = useSelector((state) => state.auth);
	const [pendingStepKey, setPendingStepKey] = useState(null);
	/** 点击后立刻视为已完成，直到详情缓存跟上（防慢网连点，对齐收款乐观锁） */
	const [optimisticCompletedKeys, setOptimisticCompletedKeys] = useState([]);
	/** 乐观完成态的时间/操作人（与真实验收展示同结构，禁止只显示「已验收」） */
	const [optimisticMetaByKey, setOptimisticMetaByKey] = useState({});
	const fulfillmentLockRef = useRef(false);
	const releaseFulfillmentLock = useCallback(() => {
		fulfillmentLockRef.current = false;
	}, []);
	const clearPendingStep = useCallback(() => {
		releaseFulfillmentLock();
		setPendingStepKey(null);
	}, [releaseFulfillmentLock]);
	const [showMoreDeliverySteps, setShowMoreDeliverySteps] = useState(false);
	const {
		order: {
			_id,
			isPlaceOrderPaid,
			isDelivered,
			deliveredAt,
			deliveredResult,
			isAccepted,
			acceptedAt,
			acceptedResult,
			scheduleNodes = [],
			approvalStatus,
		},
		isOrderReady,
		refetchProcessingOrderLists,
		enablePendingLoad,
	} = useOrderContext();
	const collaborationLocked = useOrderCollaborationLock();
	const isApprovalRejected = approvalStatus === 'reject';
	const actionDisabled = !isOrderReady || collaborationLocked || isApprovalRejected;

	const [deliverOrder, { isLoading: loadingDeliver }] = useDeliverOrderMutation();
	const [acceptOrder, { isLoading: loadingAccept }] = useAcceptOrderMutation();

	const fulfillmentBusy = Boolean(pendingStepKey) || loadingDeliver || loadingAccept;

	const rememberOptimisticStepMeta = useCallback((stepKey, data) => {
		if (!stepKey) return;
		const isAcceptStep = stepKey.startsWith('accept');
		const completedAt = isAcceptStep
			? pickValidDate(data?.acceptedAt, data?.acceptedResult?.update_time)
			: pickValidDate(data?.deliveredAt, data?.deliveredResult?.update_time);
		const result = isAcceptStep ? data?.acceptedResult : data?.deliveredResult;
		setOptimisticMetaByKey((prev) => ({
			...prev,
			[stepKey]: {
				completedAt: completedAt || new Date().toISOString(),
				result: result?.operator
					? result
					: {
							operator: userInfo?.name || '当前用户',
							update_time: completedAt || new Date().toISOString(),
						},
			},
		}));
	}, [userInfo?.name]);

	/** 流程写成功后：以 mutation 响应写缓存；勿立刻 refetch，避免发货后的 GET 盖掉紧随其后的验收 patch */
	const applyWorkflowMutationResult = useCallback(
		(data, stepKey) => {
			if (!_id || !data) return;
			patchOrderDetailsCache(dispatch, _id, data, ['workflow']);
			rememberOptimisticStepMeta(stepKey, data);
		},
		[_id, dispatch, rememberOptimisticStepMeta],
	);

	// 获取所有发货/提供服务付款节点
	const deliveryNodes = useMemo(() => {
		return scheduleNodes.filter((node) => node.title === '发货/提供服务');
	}, [scheduleNodes]);

	// 获取所有验收付款节点
	const acceptNodes = useMemo(() => {
		return scheduleNodes.filter((node) => node.title === '验收');
	}, [scheduleNodes]);

	const deliveryDoneForUi = useMemo(
		() => isScheduleGroupDone(deliveryNodes, isDelivered, 'delivery', optimisticCompletedKeys, pendingStepKey),
		[deliveryNodes, isDelivered, optimisticCompletedKeys, pendingStepKey],
	);
	const acceptDoneForUi = useMemo(
		() => isScheduleGroupDone(acceptNodes, isAccepted, 'accept', optimisticCompletedKeys, pendingStepKey),
		[acceptNodes, isAccepted, optimisticCompletedKeys, pendingStepKey],
	);

	// 动态生成步骤配置
	const steps = useMemo(() => {
		const allSteps = [];

		// 生成发货/提供服务步骤
		if (deliveryNodes.length > 0) {
			// 如果有付款节点，根据节点数量生成步骤
			deliveryNodes.forEach((node, index) => {
				const isFirstDelivery = index === 0;
				const prevDeliveryCompleted = index === 0 ? isPlaceOrderPaid : deliveryNodes[index - 1]?.isCompleted || false;
				const stepKey = `delivery-${node._id || index}`;
				const optimisticMeta = optimisticMetaByKey[stepKey];
				const stepCompleted = node.isCompleted || (deliveryNodes.length === 1 && isDelivered) || optimisticCompletedKeys.includes(stepKey);

				// 优先使用节点的 completedResult，如果没有则使用全局的 deliveredResult（仅当所有发货节点都完成时）
				let result = null;
				if (node.completedResult) {
					result = {
						operator: node.completedResult.operator_name || node.completedResult.operator,
					};
				} else if (isFirstDelivery && deliveryDoneForUi && deliveredResult) {
					result = deliveredResult;
				} else if (optimisticMeta?.result) {
					result = optimisticMeta.result;
				}

				allSteps.push({
					key: stepKey,
					title: deliveryNodes.length > 1 ? `发货/提供服务 ${index + 1}` : '发货/提供服务',
					icon: 'truck',
					isCompleted: stepCompleted,
					isCurrent: prevDeliveryCompleted && !stepCompleted && !deliveryDoneForUi,
					canProceed: prevDeliveryCompleted,
					completedAt: resolveStepCompletedAt(node, deliveredAt, deliveredResult) || optimisticMeta?.completedAt,
					result,
					scheduleNode: node._id,
					scheduleNodeData: node,
					messages: {
						waiting: isFirstDelivery ? '确认收款后发货/提供服务' : '上一个发货步骤完成后可进行此步骤',
						pending: '待发货/提供服务',
						completed: '已发货/提供服务在',
						tooltip: '确认发货/提供服务',
					},
				});
			});
		} else {
			const stepKey = 'delivery-default';
			const optimisticMeta = optimisticMetaByKey[stepKey];
			const stepCompleted = isDelivered || optimisticCompletedKeys.includes(stepKey);
			// 如果没有付款节点，使用全局状态显示一个发货步骤
			allSteps.push({
				key: stepKey,
				title: '发货/提供服务',
				icon: 'truck',
				isCompleted: stepCompleted,
				isCurrent: isPlaceOrderPaid && !stepCompleted,
				canProceed: isPlaceOrderPaid,
				completedAt: resolveStepCompletedAt(null, deliveredAt, deliveredResult) || optimisticMeta?.completedAt,
				result: deliveredResult || optimisticMeta?.result || null,
				scheduleNode: null,
				scheduleNodeData: null,
				messages: {
					waiting: '确认收款后发货',
					pending: '待发货/提供服务',
					completed: '已发货/提供服务在',
					tooltip: '确认发货',
				},
			});
		}

		// 生成验收步骤
		if (acceptNodes.length > 0) {
			// 如果有付款节点，根据节点数量生成步骤
			acceptNodes.forEach((node, index) => {
				const isFirstAccept = index === 0;
				const prevAcceptCompleted = index === 0 ? deliveryDoneForUi : acceptNodes[index - 1]?.isCompleted || false;
				const stepKey = `accept-${node._id || index}`;
				const optimisticMeta = optimisticMetaByKey[stepKey];
				const stepCompleted = node.isCompleted || (acceptNodes.length === 1 && isAccepted) || optimisticCompletedKeys.includes(stepKey);

				// 优先使用节点的 completedResult，如果没有则使用全局的 acceptedResult（仅当所有验收节点都完成时）
				let result = null;
				if (node.completedResult) {
					result = {
						operator: node.completedResult.operator_name || node.completedResult.operator,
					};
				} else if (isFirstAccept && acceptDoneForUi && acceptedResult) {
					result = acceptedResult;
				} else if (optimisticMeta?.result) {
					result = optimisticMeta.result;
				}

				allSteps.push({
					key: stepKey,
					title: acceptNodes.length > 1 ? `验收 ${index + 1}` : '验收',
					icon: 'fa-box-open',
					isCompleted: stepCompleted,
					isCurrent: prevAcceptCompleted && !stepCompleted && !acceptDoneForUi,
					canProceed: prevAcceptCompleted,
					completedAt: resolveStepCompletedAt(node, acceptedAt, acceptedResult) || optimisticMeta?.completedAt,
					result,
					scheduleNode: node._id,
					scheduleNodeData: node,
					messages: {
						waiting: isFirstAccept ? '发货后可验收' : '上一个验收步骤完成后可进行此步骤',
						pending: '待验收',
						completed: '已验收在',
						tooltip: '确认验收',
					},
				});
			});
		} else {
			const stepKey = 'accept-default';
			const optimisticMeta = optimisticMetaByKey[stepKey];
			const stepCompleted = isAccepted || optimisticCompletedKeys.includes(stepKey);
			// 如果没有付款节点，使用全局状态显示一个验收步骤
			allSteps.push({
				key: stepKey,
				title: '验收',
				icon: 'fa-box-open',
				isCompleted: stepCompleted,
				isCurrent: deliveryDoneForUi && !stepCompleted,
				canProceed: deliveryDoneForUi,
				completedAt: resolveStepCompletedAt(null, acceptedAt, acceptedResult) || optimisticMeta?.completedAt,
				result: acceptedResult || optimisticMeta?.result || null,
				scheduleNode: null,
				scheduleNodeData: null,
				messages: {
					waiting: '发货后可验收',
					pending: '待验收',
					completed: '已验收在',
					tooltip: '确认验收',
				},
			});
		}

		// 添加完成步骤（发货和验收是必要步骤，所以完成步骤总是显示）
		allSteps.push({
			key: 'complete',
			title: '物流完成',
			icon: 'fa-thumbs-up',
			isCompleted: deliveryDoneForUi && acceptDoneForUi,
			isCurrent: false,
			canProceed: deliveryDoneForUi && acceptDoneForUi,
			completedAt: pickValidDate(
				acceptedAt,
				acceptedResult?.update_time,
				optimisticMetaByKey['accept-default']?.completedAt,
				deliveredAt,
				deliveredResult?.update_time,
			),
			result: null,
			scheduleNode: null,
			scheduleNodeData: null,
			messages: {
				waiting: '待物流完成',
				pending: null,
				completed: '物流已完成在',
				tooltip: null,
			},
		});

		return allSteps;
	}, [
		deliveryNodes,
		acceptNodes,
		isPlaceOrderPaid,
		deliveryDoneForUi,
		acceptDoneForUi,
		isDelivered,
		deliveredAt,
		deliveredResult,
		isAccepted,
		acceptedAt,
		acceptedResult,
		optimisticCompletedKeys,
		optimisticMetaByKey,
	]);

	useEffect(() => {
		if (!pendingStepKey) return;
		const step = steps.find((s) => s.key === pendingStepKey);
		if (step?.isCompleted && !optimisticCompletedKeys.includes(pendingStepKey)) {
			clearPendingStep();
		}
	}, [steps, pendingStepKey, clearPendingStep, optimisticCompletedKeys]);

	useEffect(() => {
		setOptimisticCompletedKeys((prev) => {
			if (prev.length === 0) return prev;
			const next = prev.filter((key) => {
				if (key.startsWith('accept')) {
					if (isAccepted) return false;
					if (acceptNodes.length === 1 && acceptNodes[0]?.isCompleted) return false;
					return true;
				}
				if (key.startsWith('delivery')) {
					if (isDelivered) return false;
					if (deliveryNodes.length === 1 && deliveryNodes[0]?.isCompleted) return false;
					return true;
				}
				return true;
			});
			return next.length === prev.length ? prev : next;
		});
		setOptimisticMetaByKey((prev) => {
			const keys = Object.keys(prev);
			if (keys.length === 0) return prev;
			const next = { ...prev };
			let changed = false;
			for (const key of keys) {
				if (key.startsWith('accept') && isAccepted) {
					delete next[key];
					changed = true;
				}
				if (key.startsWith('delivery') && isDelivered) {
					delete next[key];
					changed = true;
				}
			}
			return changed ? next : prev;
		});
	}, [isAccepted, isDelivered, acceptNodes, deliveryNodes]);

	const deliverySteps = useMemo(() => steps.filter((step) => step.key.startsWith('delivery')), [steps]);
	const acceptSteps = useMemo(() => steps.filter((step) => step.key.startsWith('accept')), [steps]);
	const completeStep = useMemo(() => steps.find((step) => step.key === 'complete') ?? null, [steps]);
	const primaryDeliverySteps = deliveryCompact ? deliverySteps.slice(0, DELIVERY_FOCUS_VISIBLE_STEP_COUNT) : [];
	const collapsedDeliverySteps = deliveryCompact ? deliverySteps.slice(DELIVERY_FOCUS_VISIBLE_STEP_COUNT) : [];
	const hasCollapsedDeliverySteps = deliveryCompact && (collapsedDeliverySteps.length > 0 || acceptSteps.length > 0 || Boolean(completeStep));

	const visibleSteps = useMemo(() => {
		// 待验收主区：展示完整物流链（发货 + 验收 + 完成），便于核对后再操作验收
		if (pendingStepFocus === 'accept') {
			return steps;
		}
		if (deliveryCompact) {
			return [];
		}
		return steps;
	}, [deliveryCompact, pendingStepFocus, steps]);

	// 处理发货/提供服务（后端一次 save：应收 + 节点 + 发货状态）
	const handleDeliver = useCallback(
		async (step) => {
			if (!step?.key || fulfillmentLockRef.current || fulfillmentBusy || optimisticCompletedKeys.includes(step.key)) return;
			if (!_id || !isPlaceOrderPaid) {
				toast.warning('请先确认预付款已收到');
				return;
			}

			fulfillmentLockRef.current = true;
			setPendingStepKey(step.key);
			setOptimisticCompletedKeys((prev) => (prev.includes(step.key) ? prev : [...prev, step.key]));
			markLocalOrderMutation(_id);

			try {
				const node = step.scheduleNode ? step.scheduleNodeData : null;
				const data = await deliverOrder({
					orderId: _id,
					...(node?._id ? { scheduleNode: node._id } : {}),
				}).unwrap();

				applyWorkflowMutationResult(data, step.key);
				toast.success(node ? '订单已发货/提供服务, 开始计算账期' : '订单已发货/提供服务');
				clearPendingStep();
				enablePendingLoad?.('legacyBuyer');
				refetchProcessingOrderLists?.();
				socket.emit('refresh processing orderlist');
				socket.emit('refresh dashboard stats');
			} catch (err) {
				setOptimisticCompletedKeys((prev) => prev.filter((key) => key !== step.key));
				setOptimisticMetaByKey((prev) => {
					if (!(step.key in prev)) return prev;
					const next = { ...prev };
					delete next[step.key];
					return next;
				});
				clearPendingStep();
				toast.error(err?.data?.message || err?.message || '发货订单失败，请稍后重试');
			}
		},
		[
			_id,
			isPlaceOrderPaid,
			fulfillmentBusy,
			optimisticCompletedKeys,
			deliverOrder,
			applyWorkflowMutationResult,
			clearPendingStep,
			enablePendingLoad,
			refetchProcessingOrderLists,
		],
	);

	// 处理验收（后端一次 save：应收 + 节点 + 验收状态）
	const handleAccept = useCallback(
		async (step) => {
			if (!step?.key || fulfillmentLockRef.current || fulfillmentBusy || optimisticCompletedKeys.includes(step.key)) return;
			if (!_id || !isPlaceOrderPaid) {
				return;
			}

			fulfillmentLockRef.current = true;
			setPendingStepKey(step.key);
			setOptimisticCompletedKeys((prev) => (prev.includes(step.key) ? prev : [...prev, step.key]));
			markLocalOrderMutation(_id);

			try {
				const node = step.scheduleNode ? step.scheduleNodeData : null;
				const data = await acceptOrder({
					orderId: _id,
					...(node?._id ? { scheduleNode: node._id } : {}),
				}).unwrap();

				applyWorkflowMutationResult(data, step.key);
				toast.success(node ? '订单已验收, 开始计算账期' : '订单已验收完毕');
				clearPendingStep();
				refetchProcessingOrderLists?.();
				socket.emit('refresh processing orderlist');
				socket.emit('refresh dashboard stats');
			} catch (err) {
				setOptimisticCompletedKeys((prev) => prev.filter((key) => key !== step.key));
				setOptimisticMetaByKey((prev) => {
					if (!(step.key in prev)) return prev;
					const next = { ...prev };
					delete next[step.key];
					return next;
				});
				clearPendingStep();
				toast.error(err?.data?.message || err?.message || '验收订单失败，请稍后重试');
			}
		},
		[_id, isPlaceOrderPaid, fulfillmentBusy, optimisticCompletedKeys, acceptOrder, applyWorkflowMutationResult, clearPendingStep, refetchProcessingOrderLists],
	);

	const canDeliver = useMemo(() => canDeliverOrder(userInfo), [userInfo]);
	const canAccept = useMemo(() => canAcceptOrder(userInfo), [userInfo]);

	/**
	 * 渲染步骤内容
	 */
	const renderStepContent = (step) => {
		if (!step.isCompleted) {
			return <p className='fs-10 text-600 mb-0'>{step.messages.pending}</p>;
		}

		const completedAtLabel = formatDate(step.completedAt);

		return (
			<div className='d-flex flex-column align-items-end'>
				{completedAtLabel !== '-' && <p className='fs-11 text-600 mb-0'>{completedAtLabel}</p>}
				{step.result?.operator && step.messages.tooltip && (
					<div className='text-end'>
						<OverlayTrigger
							placement='top'
							overlay={
								<Tooltip style={{ position: 'fixed' }} id={`tooltip-${step.key}`} className='fs-11'>
									订单由<span className='mx-1'>{step.result.operator}</span>
									{step.messages.tooltip}
								</Tooltip>
							}
						>
							<FontAwesomeIcon icon='user-circle' className='text-primary me-1 fs-11' />
						</OverlayTrigger>
						<span className='text-primary-emphasis fs-11'>
							{step.result.operator}
							{step.messages.tooltip}
						</span>
					</div>
				)}
			</div>
		);
	};

	const renderStepList = (stepList, { isLastSection = true } = {}) =>
		stepList.map((step, index) => {
			const isLastRow = index === stepList.length - 1;
			const outerSpacing = !isLastRow || !isLastSection;
			const innerBorder = !isLastRow;
			const isDeliveryStep = step.key.startsWith('delivery');
			const isAcceptStep = step.key.startsWith('accept');
			const canAct = !viewOnly && ((isDeliveryStep && canDeliver) || (isAcceptStep && canAccept));
			const isStepDone = step.isCompleted;
			const isStepPending = pendingStepKey === step.key;
			const showPendingHint = !isStepDone && step.canProceed && !canAct && Boolean(step.messages?.pending);
			return (
				<Row
					key={step.key}
					className={classNames('g-3 recent-activity-timeline recent-activity-timeline-primary', {
						'pb-x1': outerSpacing,
						'recent-activity-timeline-past': isStepDone,
						'recent-activity-timeline-current': !isStepDone && step.isCurrent,
					})}
				>
					<Col xs='auto' className='ps-4 ms-2'>
						<div className='ps-2'>
							<div className='icon-item icon-item-sm rounded-circle bg-200 shadow-none'>
								<FontAwesomeIcon icon={step.icon} className='text-primary' />
							</div>
						</div>
					</Col>
					<Col>
						<Row className={classNames('g-3', { 'border-bottom pb-x1': innerBorder })}>
							<Col>
								<div className='mb-2'>
									<h6>{step.title}</h6>
								</div>

								{!step.canProceed && <p className='fs-10 text-600 mb-0'>{step.messages.waiting}</p>}
							</Col>
							<Col xs='auto'>
								{!isApprovalRejected && !isStepDone && step.canProceed && canAct && (
									<OrderWorkflowActionButton
										pending={isStepPending}
										icon={isDeliveryStep ? 'fa-truck' : 'fa-box-open'}
										disabled={actionDisabled || fulfillmentBusy || approvalStatus === 'inProcessOfApproval'}
										onClick={() => {
											if (isDeliveryStep) {
												handleDeliver(step);
											} else if (isAcceptStep) {
												handleAccept(step);
											}
										}}
									>
										{isDeliveryStep ? '发货' : '验收'}
									</OrderWorkflowActionButton>
								)}
								{showPendingHint && (
									<p className='fs-10 text-warning mb-0 text-end' role='status'>
										{step.messages.pending}
									</p>
								)}
								{isStepDone &&
									(isStepPending ? (
										<div className='text-end' aria-busy='true' aria-live='polite'>
											<span className='d-inline-flex align-items-center gap-1 fs-11 text-warning'>
												<Spinner animation='border' size='sm' role='status' />
												处理中…
											</span>
										</div>
									) : (
										renderStepContent(step)
									))}
							</Col>
						</Row>
					</Col>
				</Row>
			);
		});

	return (
		<div className='position-relative px-0 pt-2 pb-1'>
			<div className='px-1'>
				{deliveryCompact ? (
					<>
						{renderStepList(primaryDeliverySteps, { isLastSection: !hasCollapsedDeliverySteps })}
						{hasCollapsedDeliverySteps ? (
							<>
								<Button
									variant='link'
									size='sm'
									className='d-inline-flex align-items-center gap-1 px-0 mt-2 mb-1 fs-10 text-decoration-none'
									onClick={() => setShowMoreDeliverySteps((v) => !v)}
									aria-expanded={showMoreDeliverySteps}
								>
									<FontAwesomeIcon icon={showMoreDeliverySteps ? 'chevron-up' : 'chevron-down'} className='fa-fw' />
									{showMoreDeliverySteps ? '收起其余物流节点' : '查看其余物流节点'}
								</Button>
								<Collapse in={showMoreDeliverySteps}>
									<div className='pt-2'>
										{renderStepList(collapsedDeliverySteps, {
											isLastSection: acceptSteps.length === 0 && !completeStep,
										})}
										{renderStepList(acceptSteps, { isLastSection: !completeStep })}
										{completeStep ? renderStepList([completeStep], { isLastSection: true }) : null}
									</div>
								</Collapse>
							</>
						) : null}
					</>
				) : (
					renderStepList(visibleSteps)
				)}
			</div>
		</div>
	);
};

OrderDeliveryStatus.propTypes = {
	pendingStepFocus: PropTypes.oneOf(['delivery', 'accept']),
};

export default OrderDeliveryStatus;
